import apiClient, { ApiError } from './client';
import { API_ENDPOINTS } from './config';
import type {
	Service,
	ServiceTask,
	Customer,
	Appointment,
	AppointmentStatus,
	CreateAppointmentRequest,
	AppointmentsListResponse,
	AppointmentsQueryParams,
	DashboardAppointmentsResponse,
	DashboardAppointmentsQueryParams,
} from '../types/appointments';
import type { ProviderService, TaskMethod } from '../types/provider';

export const appointmentsApi = {
	/**
	 * Get services for a location
	 */
	getServices: async (locationId: number): Promise<Service[]> => {
		return apiClient.get<Service[]>(
			`${API_ENDPOINTS.SERVICE.MY_SERVICES}?locationId=${locationId}`
		);
	},

	/**
	 * Lookup customer by email
	 * Returns null if customer not found (404)
	 */
	lookupCustomerByEmail: async (email: string): Promise<Customer | null> => {
		try {
			return await apiClient.get<Customer>(
				`${API_ENDPOINTS.CUSTOMER.LOOKUP_BY_EMAIL}?email=${encodeURIComponent(email)}`
			);
		} catch (error) {
			if (error instanceof ApiError && error.isNotFound()) {
				return null;
			}
			throw error;
		}
	},

	/**
	 * Lookup customer by phone
	 * Returns null if customer not found (404)
	 */
	lookupCustomerByPhone: async (phone: string): Promise<Customer | null> => {
		try {
			return await apiClient.get<Customer>(
				`${API_ENDPOINTS.CUSTOMER.LOOKUP_BY_PHONE}?phone=${encodeURIComponent(phone)}`
			);
		} catch (error) {
			if (error instanceof ApiError && error.isNotFound()) {
				return null;
			}
			throw error;
		}
	},

	/**
	 * Create a new appointment
	 */
	createAppointment: async (data: CreateAppointmentRequest): Promise<Appointment> => {
		return apiClient.post<Appointment>(API_ENDPOINTS.APPOINTMENT.BASE, data);
	},

	/**
	 * Get dashboard appointments with filtering and pagination
	 */
	getDashboardAppointments: async (
		params: DashboardAppointmentsQueryParams
	): Promise<DashboardAppointmentsResponse> => {
		const queryParams = new URLSearchParams();
		queryParams.append('locationId', String(params.locationId));
		if (params.period) queryParams.append('period', params.period);
		if (params.startDate) queryParams.append('startDate', params.startDate);
		if (params.endDate) queryParams.append('endDate', params.endDate);
		if (params.status) queryParams.append('status', params.status);
		if (params.staffId !== undefined) queryParams.append('staffId', String(params.staffId));
		if (params.customerName) queryParams.append('customerName', params.customerName);
		if (params.page !== undefined) queryParams.append('page', String(params.page));
		if (params.size !== undefined) queryParams.append('size', String(params.size));

		return apiClient.get<DashboardAppointmentsResponse>(
			`${API_ENDPOINTS.APPOINTMENT.DASHBOARD_LIST}?${queryParams.toString()}`
		);
	},

	/**
	 * Update appointment status
	 */
	updateStatus: async (id: number, status: AppointmentStatus): Promise<Appointment> => {
		return apiClient.patch<Appointment>(API_ENDPOINTS.APPOINTMENT.UPDATE_STATUS(id, status));
	},

	/**
	 * Reschedule an appointment to a new date/time
	 */
	reschedule: async (id: number, appointmentDateTime: string): Promise<Appointment> => {
		return apiClient.patch<Appointment>(API_ENDPOINTS.APPOINTMENT.RESCHEDULE(id), {
			appointmentDateTime,
		});
	},

	/**
	 * Schedule staff to an appointment
	 */
	schedule: async (
		id: number,
		taskAssignments: { serviceTaskId: number; staffIds: number[] }[]
	): Promise<Appointment> => {
		return apiClient.post<Appointment>(API_ENDPOINTS.APPOINTMENT.SCHEDULE(id), {
			taskAssignments,
		});
	},

	/**
	 * Get service tasks with task pricing for a location
	 */
	getServiceTasks: async (locationId: number): Promise<ServiceTask[]> => {
		return apiClient.get<ServiceTask[]>(
			`${API_ENDPOINTS.SERVICE_TASK}?locationId=${locationId}`
		);
	},

	/**
	 * Update service details for an appointment (service mapping)
	 */
	updateServiceDetails: async (
		id: number,
		data: { serviceId: number; serviceName: string; size: string; hairLength: string }
	): Promise<Appointment> => {
		return apiClient.patch<Appointment>(API_ENDPOINTS.APPOINTMENT.SERVICE_DETAILS(id), data);
	},

	/**
	 * Get available services (not yet mapped to tasks) for a location
	 */
	getAvailableServices: async (locationId: number): Promise<ProviderService[]> => {
		return apiClient.get<ProviderService[]>(
			`${API_ENDPOINTS.SERVICE_TASK_AVAILABLE}?locationId=${locationId}`
		);
	},

	/**
	 * Create service tasks for selected services with a task method
	 */
	createServiceTasks: async (
		locationId: number,
		data: { method: TaskMethod; serviceIds: number[] }
	): Promise<void> => {
		await apiClient.post(`${API_ENDPOINTS.SERVICE_TASK}?locationId=${locationId}`, data);
	},

	/**
	 * Delete a service task mapping
	 */
	deleteServiceTask: async (id: number, locationId: number): Promise<void> => {
		await apiClient.delete(`${API_ENDPOINTS.SERVICE_TASK}/${id}?locationId=${locationId}`);
	},

	/**
	 * Get paginated list of appointments with statistics
	 */
	getAppointments: async (
		params?: AppointmentsQueryParams
	): Promise<AppointmentsListResponse> => {
		const queryParams = new URLSearchParams();
		if (params?.page !== undefined) queryParams.append('page', String(params.page));
		if (params?.size !== undefined) queryParams.append('size', String(params.size));

		const queryString = queryParams.toString();
		const url = queryString
			? `${API_ENDPOINTS.APPOINTMENT.BASE}?${queryString}`
			: API_ENDPOINTS.APPOINTMENT.BASE;

		return apiClient.get<AppointmentsListResponse>(url);
	},
};

// Re-export ApiError for convenience
export { ApiError };
